import { Fragment, forwardRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { SourcePropertyFragment } from "@/apollo/types";
import { Badge } from "@/components/elements/Badge";
import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import mergeRefs from "@/utils/mergeRefs";
import { Listbox, ListboxProps, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

type PropertySelectorProps = ListboxProps<
  "div",
  SourcePropertyFragment | null,
  SourcePropertyFragment
> & {
  options: SourcePropertyFragment[];
};

export const SourcePropertySelector = forwardRef<
  HTMLButtonElement,
  PropertySelectorProps
>(({ options, ...props }, ref) => {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-start",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  return (
    <Listbox as="div" by="name" className="relative" {...props}>
      <Listbox.Button
        ref={mergeRefs(ref, setReferenceElement)}
        className="relative flex h-10 w-full cursor-default items-center overflow-hidden rounded-sm border text-left text-sm focus-within:outline focus-within:outline-blue-600"
      >
        <div className="w-full pl-4 pr-8">
          {props.value ? (
            <div className="flex gap-2">
              <Tooltip content={props.value?.name ?? null}>
                <div className="truncate">{props.value?.name}</div>
              </Tooltip>
              <div className="ml-auto flex items-center">
                <Badge
                  variant="secondary"
                  className="px-0.5 py-0 uppercase leading-tight"
                >
                  {props.value.type}
                </Badge>
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">Select a column...</span>
          )}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </Listbox.Button>
      {createPortal(
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            ref={(elm) => setPopperElement(elm as HTMLUListElement | null)}
            style={styles.popper}
            {...attributes.popper}
            className="absolute z-10 max-h-80 w-full max-w-md overflow-auto rounded-sm bg-background py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900"
          >
            {options.length === 0 && (
              <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
                No source properties available.
              </div>
            )}
            {options.map((prop) => (
              <Listbox.Option
                key={prop.name}
                value={prop}
                className={({ active, selected }) =>
                  cn(`relative cursor-default select-none px-4 py-2`, {
                    "bg-blue-500 text-white": active || selected,
                  })
                }
              >
                {({ selected }) => (
                  <>
                    <div className="flex gap-2">
                      <Tooltip content={prop.name}>
                        <div
                          className={cn("truncate", {
                            "font-medium": selected,
                          })}
                        >
                          {prop.name}
                        </div>
                      </Tooltip>
                      <div className="ml-auto">
                        <Badge
                          variant="secondary"
                          className="px-0.5 py-0 leading-tight"
                        >
                          {prop.type}
                        </Badge>
                      </div>
                    </div>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>,
        document.body,
      )}
    </Listbox>
  );
});
